import { AbstractRenderer, Graphics, RenderTexture, Texture } from "pixi.js"

// The Bonecaller's boundary flames: a pool of soft rising particles in the ritual's green and violet,
// baked once into RenderTextures and played back by plain AnimatedSprites (see bonecaller_warning).
// This is the bake the campfire used before it got pixel-art flames of its own (fire_art.ts); the
// ritual keeps the soft look, which reads as spell-light rather than burning wood.
//
// SEAMLESS LOOP: every particle is a pure function of a loop phase u in [0,1): its age is
// frac(u - birthPhase), so the frame at u=1 is identical to u=0. Particles fade to zero alpha just
// before their age wraps (top -> bottom), and staggered birth phases mean no two reset together.

const FRAME_W = 48
const FRAME_H = 64
const FRAME_COUNT = 30
const PARTICLE_COUNT = 52

// Young -> old: pale venom green -> green -> violet -> deep plum.
const ritualColor = (lifeFrac: number): number =>
    lifeFrac > 0.85 ? 0xd6ffbe : lifeFrac > 0.58 ? 0x82ef45 : lifeFrac > 0.3 ? 0x9654d8 : 0x43145f

interface LoopParticle {
    birthPhase: number // when in the loop this particle is "youngest" (at the base)
    baseX: number // horizontal spawn offset from centre
    baseY: number // vertical spawn (near the bottom)
    rise: number // how far it travels up over one lifetime (varies => tongues of different height)
    size: number // base radius
    drift: number // slow lateral drift over life
    converge: number // how strongly it pulls toward centre as it rises (narrows the flame upward)
    wobbleAmp: number // lateral wobble amplitude
    wobbleFreq: number // lateral wobble cycles per lifetime
    wobblePhase: number
}

const frac = (n: number): number => n - Math.floor(n)

const makeLoopParticles = (): LoopParticle[] => {
    const cx = FRAME_W / 2
    const particles: LoopParticle[] = []
    for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
            // Evenly staggered around the loop (+ small jitter) so density is steady, not pulsing.
            birthPhase: frac(i / PARTICLE_COUNT + (Math.random() - 0.5) * (0.4 / PARTICLE_COUNT)),
            baseX: cx + (Math.random() - 0.5) * FRAME_W * 0.3,
            baseY: FRAME_H - Math.random() * 5,
            rise: FRAME_H * (0.55 + Math.random() * 0.5),
            size: 3.5 + Math.random() * 3.5,
            drift: (Math.random() - 0.5) * FRAME_W * 0.1,
            converge: 0.3 + Math.random() * 0.4,
            wobbleAmp: 1.5 + Math.random() * 3,
            wobbleFreq: 0.5 + Math.random() * 1.5,
            wobblePhase: Math.random() * Math.PI * 2,
        })
    }
    return particles
}

// One loop frame: each particle a soft circle whose alpha envelope is 0 at both ends of its life
// (so the top->bottom wrap is invisible) and brightest near the base.
const drawLoopFrame = (g: Graphics, particles: LoopParticle[], phase: number) => {
    g.clear()
    const cx = FRAME_W / 2
    for (const p of particles) {
        const age = frac(phase - p.birthPhase) // 0 (youngest, at base) -> 1 (oldest, at top)
        const lifeFrac = 1 - age
        const fadeIn = Math.min(1, age / 0.12)
        const env = fadeIn * (1 - age)
        if (env <= 0.02) {
            continue
        }
        const y = p.baseY - p.rise * age
        const xBase = p.baseX + (cx - p.baseX) * (age * p.converge) + p.drift * age
        const x = xBase + p.wobbleAmp * Math.sin(p.wobbleFreq * age * Math.PI * 2 + p.wobblePhase)
        const r = p.size * (0.5 + 0.5 * lifeFrac)
        g.beginFill(ritualColor(lifeFrac), env * 0.9)
        g.drawCircle(x, y, r)
        g.endFill()
    }
}

let cachedFrames: Texture[] | undefined

/** FRAME_COUNT seamless loop frames, baked on first use and shared by every ritual flame after. */
export const bakeRitualFlameFrames = (renderer: AbstractRenderer): Texture[] => {
    if (cachedFrames) {
        return cachedFrames
    }
    const particles = makeLoopParticles()
    const g = new Graphics()
    const frames: Texture[] = []
    for (let i = 0; i < FRAME_COUNT; i++) {
        drawLoopFrame(g, particles, i / FRAME_COUNT)
        const rt = RenderTexture.create({ width: FRAME_W, height: FRAME_H })
        renderer.render(g, { renderTexture: rt, clear: true })
        frames.push(rt)
    }
    g.destroy()
    cachedFrames = frames
    return frames
}
